import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import useChartSlice from "store/slice/charts/useChartSlice";

// Components
import {
    fetchTenureChart,
    getLD13Filters,
    ILD13SelectedLegend,
    LD13ChartSeries,
    setLD13,
    setLD13Filters,
} from "store/slice/charts";
import { OrderTable, Order } from "components/SortMenu/SortMenu";

// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks";
import useGraphsFetch from "utils/hooks/useGraphsFetch";
import useTenureLegend from "./useTenureLegend";

// Store
import { toggleLoader } from "store/slice/UI";

// Config
import {
    buildTransactionLine,
    ChartWithCustomLine,
    getOptions,
    labelFormatter,
    mapPersonDataById,
    OP_DIM,
    OP_FULL,
} from "../tenureConfig";

// Utils
import { isEmpty, isEqual } from "lodash";
import { format, subYears } from "date-fns";
import { unwrapResult } from "@reduxjs/toolkit";

// Types
import { LD13Chart } from "store/slice/charts/chartsSlice.types";
import { tenureChartOrderBy, TenureChartPayload } from "services/tenureChart";
import { IBaseChartProps } from "components/Charts/chart.types";
import { withOpacity } from "utils/utils";
import { TCustomiseLabelsOptions } from "../../components/CustomiseLabels";
import { useTenureLabelsContext } from "../context/TenureLabelsContext";

export type tenureChartOrderByType = typeof tenureChartOrderBy[number];
export type transactionPopoverProps = { open: boolean; id: number; top: number; left: number };

const initialYearDifference = 15;
const today = new Date();
const tenureChartOrder = {
    asc: "ascending" as const,
    desc: "descending" as const,
};

const isTenureChartOrderBy = (value: string | number): value is tenureChartOrderByType => {
    return tenureChartOrderBy.includes(value as tenureChartOrderByType);
};

const useTenure = ({ getData, hideRefSeries }: Omit<IBaseChartProps, "width" | "height">) => {
    const dispatch = useAppDispatch();

    const { target_company_id, target_company_board } = useGraphsFetch();

    const tenureChartData = useChartSlice(getData) as LD13Chart;
    const LD13Filters = useAppSelector(getLD13Filters);

    const hasSharedHistories = Boolean(tenureChartData?.hasSharedHistories);

    const LD13SelectedLegendRef = useRef(LD13Filters?.LD13SelectedLegend);
    const pointColorsRef = useRef<Record<string, string>>({});

    const chartRef = useRef<{
        chart: ChartWithCustomLine;
        container: React.RefObject<HTMLDivElement>;
    }>(null);

    const { exportWithLegend } = useTenureLegend(tenureChartData);

    const handleClickSerie = useCallback(
        (selectedLegend: ILD13SelectedLegend) => {
            const current = LD13SelectedLegendRef.current;

            if (selectedLegend.id === current?.id) {
                dispatch(setLD13Filters({ LD13SelectedLegend: { id: undefined, type: "personId" } }));
                return;
            }

            dispatch(setLD13Filters({ LD13SelectedLegend: selectedLegend }));
        },
        [dispatch]
    );

    const [openDateFilter, setOpenDateFilter] = useState(false);

    const [transactionPopover, setTransactionPopover] = useState<transactionPopoverProps>({
        open: false,
        id: 0,
        top: 0,
        left: 0,
    });

    const [dateRange, setDateRange] = useState({
        startDate: tenureChartData.xAxis.min.split("-")[0] || format(subYears(today, initialYearDifference), "yyy"),
        endDate: `${parseInt(tenureChartData.xAxis.max.split("-")[0])}` || format(today, "yyyy"),
        present: false,
    });
    const previousDateRange = useRef(dateRange);

    function handleToggleTransactionsPopover(popoverDetails: transactionPopoverProps) {
        setTransactionPopover(popoverDetails);
    }

    // --------- Customise labels ------------
    const peopleById = useMemo(() => mapPersonDataById(tenureChartData), [tenureChartData]);
    const { customiseLabels, disabledOptions, setCustomiseLabels } = useTenureLabelsContext();

    const customiseLabelsRef = useRef(customiseLabels);
    const peopleByIdRef = useRef(peopleById);

    useEffect(() => {
        customiseLabelsRef.current = customiseLabels;
    }, [customiseLabels]);

    useEffect(() => {
        peopleByIdRef.current = peopleById;
    }, [peopleById]);

    useEffect(() => {
        chartRef.current?.chart?.redraw(false);
    }, [customiseLabels, peopleById]);

    const yLabelFormatter = useCallback(function (formatterObj: Highcharts.AxisLabelsFormatterContextObject) {
        return labelFormatter(formatterObj, customiseLabelsRef.current, peopleByIdRef.current);
    }, []);

    const handleSetCustomiseLabels = (options: TCustomiseLabelsOptions) => {
        setCustomiseLabels(options);
    };

    // ----------------------------------------

    const [chartOptions, setChartOptions] = useState(
        getOptions({
            data: { ...tenureChartData },
            onClickSerie: handleClickSerie,
            toggleTransactionsPopover: handleToggleTransactionsPopover,
            yLabelFormatter,
        })
    );

    const latestSeriesRef = useRef(chartOptions.series);

    const handleSort = (direction: OrderTable, columnId: string | number) => {
        if (isTenureChartOrderBy(columnId)) {
            dispatch(
                setLD13Filters({
                    sort: { columnId, direction },
                })
            );
            handleFetchData({ sort_by: columnId, sort_direction: tenureChartOrder[direction as Order] });
        } else {
            const msg = `columnId is not a valid value: "${columnId}".`;
            if (process.env.NODE_ENV === "development") {
                throw new Error(msg);
            } else {
                console.error(msg);
            }
        }
    };

    const handleToggleDateFilter = (isOpen: boolean) => {
        setOpenDateFilter(isOpen);

        if (!isOpen && !isEqual(dateRange, previousDateRange.current)) {
            handleFetchData();
        }
    };

    const handleFetchData = async (params?: Partial<TenureChartPayload>) => {
        dispatch(toggleLoader(true));

        previousDateRange.current = dateRange;

        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");

        const { LD13 } = unwrapResult(
            await dispatch(
                fetchTenureChart({
                    company_id: target_company_id,
                    start_date: `${dateRange.startDate}-01-01`,
                    end_date: dateRange.present ? format(today, "yyyy-MM-dd") : `${dateRange.endDate}-${month}-${day}`,
                    person_ids: target_company_board.flatMap((board) => board.person_ids),
                    change: true,
                    ...(LD13Filters?.sort?.columnId ? { sort_by: LD13Filters.sort.columnId } : {}),
                    ...(LD13Filters?.sort?.direction
                        ? { sort_direction: tenureChartOrder[LD13Filters.sort.direction as Order] }
                        : {}),
                    ...params,
                })
            )
        );

        setChartOptions((prevChartOptions) => {
            const series = LD13.converted?.series.map((s) => {
                const prev = prevChartOptions.series?.[0].data?.find((p) => p.id === s.id);
                return { ...s, visible: prev?.visible, color: pointColorsRef.current[s.id] ?? s.color };
            }) as LD13ChartSeries[] | undefined;

            return getOptions({
                data: { ...LD13.converted, series } as LD13Chart | undefined,
                onClickSerie: handleClickSerie,
                toggleTransactionsPopover: handleToggleTransactionsPopover,
                yLabelFormatter,
            });
        });

        dispatch(toggleLoader(false));
    };

    const handleToggleTransactionLines = useCallback(
        (shouldShowLines: boolean | undefined) => {
            const chart = chartRef.current?.chart;
            if (!chart) return;

            const xAxis = chart.xAxis[0];

            if (shouldShowLines) {
                tenureChartData.transactions.transactions.forEach((t) => {
                    const transactionLine = buildTransactionLine(t, handleToggleTransactionsPopover);
                    xAxis.addPlotLine(transactionLine);
                });
            } else {
                xAxis.removePlotLine("transaction-line");
            }

            dispatch(
                setLD13Filters({
                    showTransactions: shouldShowLines,
                })
            );
        },
        [dispatch, tenureChartData.transactions.transactions]
    );

    const handleToggleSeries = useCallback((selectedLegend: ILD13SelectedLegend | undefined, dataUpdated = false) => {
        const chart = chartRef.current?.chart;
        if (!chart || !selectedLegend) return;

        const { id, type } = selectedLegend;

        const setOpacity = (pid?: string) => {
            const chart = chartRef.current?.chart as ChartWithCustomLine | undefined;
            if (!chart) return;

            if (dataUpdated && !isEmpty(pointColorsRef.current)) {
                chart.series.forEach((series) => {
                    series.points.forEach((point) => {
                        if (point.id) {
                            const newColor = pointColorsRef.current?.[point.id] || point.originalColor || "";
                            point.update({ color: newColor }, false);
                        }
                    });
                });

                chart.redraw(false);

                return;
            }

            const pointColors: Record<string, string> = {};

            chart.series.forEach((series) => {
                series.points.forEach((point) => {
                    const key = type === "personId" ? point.personId : point.overlapingId;

                    const base = point.originalColor ?? (point.options.color || point.color);
                    point.originalColor = base;

                    const alpha = pid === undefined ? OP_FULL : key === pid ? OP_FULL : OP_DIM;
                    const newColor = withOpacity(String(base), alpha);

                    point.update({ color: newColor }, false);
                    pointColors[String(point.id)] = newColor;
                });
            });

            pointColorsRef.current = pointColors;
            chart.redraw(false);
        };

        if (!id) {
            delete chart.currentFilteredPersonId;
            setOpacity();
            return;
        }

        if (chart.currentFilteredPersonId === id) {
            delete chart.currentFilteredPersonId;
            setOpacity();
        } else {
            chart.currentFilteredPersonId = id;
            setOpacity(id);
        }
    }, []);

    const handleToggleSharedHistory = useCallback(
        (checked: boolean | undefined) => {
            if (checked === undefined) return;

            setChartOptions((prevChartOptions) => {
                const baseSeries = prevChartOptions.series?.[0];

                if (!baseSeries) {
                    return prevChartOptions;
                }

                const newSeries = [
                    {
                        ...baseSeries,
                        data: prevChartOptions.series?.[0].data?.map((d) => {
                            const dataColor = tenureChartData.series.find((s) => s.id === d.id)?.color;
                            const chartColor = pointColorsRef.current;
                            const color = chartColor[d.id] ?? dataColor ?? "";

                            return {
                                ...d,
                                color,
                                visible: d.overlapingId ? checked : d.visible,
                            };
                        }),
                    },
                ];

                return { ...prevChartOptions, series: newSeries };
            });

            dispatch(
                setLD13Filters({
                    sharedHistory: checked,
                })
            );
        },
        [dispatch, tenureChartData.series]
    );

    useEffect(() => {
        if (!hasSharedHistories) {
            if (LD13Filters?.sharedHistory !== false) {
                handleToggleSharedHistory(false);
            }
            return;
        }

        if (LD13Filters?.sharedHistory === undefined) {
            handleToggleSharedHistory(true);
        }
    }, [hasSharedHistories, LD13Filters?.sharedHistory, handleToggleSharedHistory]);

    const handleToggleCurrentPrior = (roleType: string | undefined) => {
        if (!roleType) return;

        const newRoleType = roleType === "target_current" ? "target_previous" : "target_current";

        setChartOptions((prevChartOptions) => {
            const baseSeries = prevChartOptions.series?.[0];

            if (!baseSeries) {
                return prevChartOptions;
            }

            const newSeries = [
                {
                    ...baseSeries,
                    data: baseSeries.data?.map((d) => {
                        const dataColor = tenureChartData.series.find((s) => s.id === d.id)?.color;
                        const visibility = LD13Filters?.roleTenure === newRoleType || d.roleType === newRoleType;
                        return {
                            ...d,
                            color: pointColorsRef.current[d.id] ?? dataColor,
                            visible: !d.overlapingId ? visibility : d.visible,
                        };
                    }),
                },
            ];

            return { ...prevChartOptions, series: newSeries };
        });

        dispatch(
            setLD13Filters({
                roleTenure: LD13Filters?.roleTenure === newRoleType ? "" : newRoleType,
            })
        );
    };

    useEffect(() => {
        handleToggleSeries(LD13Filters?.LD13SelectedLegend);
        LD13SelectedLegendRef.current = LD13Filters?.LD13SelectedLegend;
    }, [LD13Filters?.LD13SelectedLegend, handleToggleSeries]);

    useEffect(() => {
        handleToggleTransactionLines(LD13Filters?.showTransactions);
    }, [LD13Filters?.showTransactions, handleToggleTransactionLines]);

    useEffect(() => {
        dispatch(setLD13Filters({ ...LD13Filters }));

        requestAnimationFrame(() => {
            handleToggleSeries(LD13Filters?.LD13SelectedLegend, true);
            handleToggleTransactionLines(LD13Filters?.showTransactions);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenureChartData]);

    useEffect(() => {
        latestSeriesRef.current = chartOptions.series;
    }, [chartOptions.series]);

    useEffect(() => {
        return () => {
            dispatch(
                setLD13({
                    ...tenureChartData,
                    series: latestSeriesRef.current?.[0]?.data || [],
                })
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        sort: LD13Filters?.sort,
        chartRef,
        dateRange,
        chartOptions,
        sharedHistory: LD13Filters?.sharedHistory ?? hasSharedHistories,
        hasSharedHistories,
        openDateFilter,
        tenureChartData,
        transactionPopover,
        roleTenure: LD13Filters?.roleTenure,
        showTransactions: LD13Filters?.showTransactions,
        customiseLabels,
        disabledOptions,
        handleSetCustomiseLabels,
        exportWithLegend,
        handleSort,
        setDateRange,
        handleToggleSharedHistory,
        handleToggleDateFilter,
        handleToggleTransactionsPopover,
        handleToggleTransactionLines,
        handleToggleCurrentPrior,
    };
};

export default useTenure;
